import { Trans } from "@lingui/react/macro";
import { format, Locale } from "date-fns";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle,
  Clock,
  MapPin,
  Music,
  User,
} from "lucide-react";
import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { useUserBookingQuery } from "../../api/bookingApi.ts";
import {
  getTranslations,
  useSupportedInstruments,
} from "../../api/instrumentApi.ts";
import { InstantRange } from "../../api/scheduleApi.ts";
import { useLanguageStore } from "../../stores/languageStore.ts";

const formatTimeSlot = (timeSlot: InstantRange, locale: Locale): string => {
  const formatTime = (date: Date) => {
    return format(date, "HH:mm", { locale });
  };

  const formatDate = (date: Date) => {
    return format(date, "EEE MMMM d", { locale });
  };

  return `${formatDate(timeSlot.from)}, ${formatTime(timeSlot.from)} - ${formatTime(timeSlot.to)}`;
};

const CheckoutSuccess: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const { language, dateFnsLocale } = useLanguageStore();

  const {
    data: booking,
    isError: bookingError,
    isLoading: bookingLoading,
  } = useUserBookingQuery(bookingId!, {
    enabled: !!bookingId,
  });

  const { data: supportedInstruments } = useSupportedInstruments();

  const teacherName = useMemo(() => booking?.teacherName, [booking]);
  const profilePictureUrl = useMemo(
    () => booking?.teacherProfilePictureUrl,
    [booking],
  );

  const getProfilePicture = () => {
    return profilePictureUrl ? (
      <img
        src={profilePictureUrl}
        alt={""}
        className="h-6 w-6 rounded-full object-cover"
      />
    ) : (
      <User className="h-6 w-6" />
    );
  };

  const handleGoToBookings = () => {
    navigate(`/mystudio/bookings`);
  };

  // Handle missing bookingId
  if (!bookingId) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-red-600" />
            <p className="font-medium text-red-800">
              <Trans>
                Invalid booking ID. Please check your link and try again.
              </Trans>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (bookingLoading) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="flex min-h-64 items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-green-500"></div>
            <p className="text-gray-600">
              <Trans>Loading booking details...</Trans>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (bookingError || !booking) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-red-600" />
            <div>
              <p className="font-medium text-red-800">
                <Trans>Error loading booking details</Trans>
              </p>
              <p className="mt-1 text-sm text-red-700">
                <Trans>
                  Your payment was successful, but we couldn't load the booking
                  details. Please check your bookings page.
                </Trans>
              </p>
            </div>
          </div>
          <button
            onClick={handleGoToBookings}
            className="bg-soluud-primary hover:bg-soluud-primary-hover active:bg-soluud-primary-active mt-4 inline-flex items-center gap-2 rounded-lg px-4 py-2 font-medium text-white shadow-sm transition-all duration-150 active:scale-[0.98]"
          >
            <Trans>Go to Booking Overview</Trans>
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-6">
      {/* Success Header */}
      <div className="mb-8 text-center">
        <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <CheckCircle className="h-10 w-10 text-green-600" />
        </div>
        <h1 className="mb-2 text-3xl font-bold text-gray-900">
          <Trans>Payment Successful!</Trans>
        </h1>
        <p className="text-lg text-gray-600">
          <Trans>Your booking has been confirmed and payment processed.</Trans>
        </p>
      </div>

      {/* Booking Details */}
      <div className="mb-8 rounded-lg border-2 border-green-200 bg-green-50 p-6">
        <div className="mb-4 flex items-center gap-2">
          <CheckCircle className="h-6 w-6 text-green-600" />
          <h2 className="text-xl font-semibold text-green-900">
            <Trans>Booking Confirmed</Trans>
          </h2>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Music className="h-6 w-6 text-green-600" />
              <div>
                <p className="text-sm text-green-700">Instrument</p>
                <p className="font-semibold text-green-900">
                  {getTranslations(
                    supportedInstruments,
                    booking.instrument,
                    language,
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {getProfilePicture()}
              <div>
                <p className="text-sm text-green-700">
                  <Trans>Teacher</Trans>
                </p>
                <p className="font-semibold text-green-900">{teacherName}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <MapPin className="h-6 w-6 text-green-600" />
              <div>
                <p className="text-sm text-green-700">Location</p>
                <p className="font-semibold text-green-900">
                  {booking?.locationName}
                </p>
              </div>
            </div>
          </div>

          <div>
            <div className="mb-3 flex items-center gap-3">
              <Clock className="h-6 w-6 text-green-600" />
              <div>
                <p className="text-sm text-green-700">
                  <Trans>Scheduled Times</Trans>
                </p>
              </div>
            </div>
            <div className="ml-9 space-y-2">
              {booking?.timeSlots.map((timeSlot, index) => (
                <div
                  key={index}
                  className="rounded bg-green-100 px-3 py-2 font-mono text-sm text-green-800"
                >
                  {formatTimeSlot(timeSlot, dateFnsLocale)}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 border-t border-green-200 pt-4">
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold text-green-900">
              <Trans>Amount Paid</Trans>
            </span>
            <span className="text-2xl font-bold text-green-900">
              €{booking.price}
            </span>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="mb-8 rounded-lg border border-gray-200 bg-gray-50 p-6">
        <h3 className="mb-3 text-lg font-semibold text-gray-900">
          <Trans>What's Next?</Trans>
        </h3>
        <div className="space-y-2 text-gray-700">
          <p>
            •{" "}
            <Trans>
              You will receive a confirmation email with all the details
            </Trans>
          </p>
          <p>
            •{" "}
            <Trans>
              Your teacher has been notified and will contact you if needed
            </Trans>
          </p>
          <p>
            •{" "}
            <Trans>
              Please arrive 5-10 minutes early for your first lesson
            </Trans>
          </p>
          <p>
            •{" "}
            <Trans>
              You can view all your bookings in your account dashboard
            </Trans>
          </p>
        </div>
      </div>

      {/* Action Button */}
      <div className="text-center">
        <button
          onClick={handleGoToBookings}
          className="bg-soluud-primary hover:bg-soluud-primary-hover active:bg-soluud-primary-active inline-flex items-center gap-2 rounded-lg px-6 py-3 text-lg font-medium text-white shadow-sm transition-all duration-150 active:scale-[0.98]"
        >
          <Trans>Go to Booking Overview</Trans>
          <ArrowRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
